import {useInvalidateQueryCache} from '@aboutyou/data-fetcher';
import {BasketPageService_GetBasketPageBulkLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.grpc.lazy';
import {CouponsService_GetWalletV2Lazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/wallet/service.grpc.lazy';
import {WalletService_GetWalletLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/service.grpc.lazy';
import {WishlistPageService_GetWishlistPageLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/service.grpc.lazy';
import {useTranslation} from '@aboutyou/i18n';
import {
  useHistory,
  useIsArticleDetailPage,
  useIsBasketPage,
  useIsWishlistPage,
} from '@aboutyou/router';
import {Text, useShowToast} from '@aboutyou/ui-components';
import React, {startTransition, useCallback, useEffect} from 'react';
import {useGetCoupons} from '../../features/Coupons/CouponWallet/hooks/useGetCoupons';
import {useActivatedCouponState} from '../../features/Coupons/useActivatedCouponState';
import {COUPON_PROMOTION_ID_QUERY_KEY} from '../../features/Coupons/useCouponPromotionIdFromQueryParams';
import {useProductIdFromSlug} from '../../features/ProductUrl';
import {MAX_TIMEOUT_WAIT_IN_MS} from '../../features/constants/constants';
import {useWalletStateManager} from '../Wallet/utils/useWalletStateManager';

const useShowCouponsExpiredToast = () => {
  const showToast = useShowToast();
  const {t} = useTranslation();

  const isAdp = useIsArticleDetailPage();
  const isBasket = useIsBasketPage();
  const isWishlist = useIsWishlistPage();
  const {isOpen: isWalletOpen} = useWalletStateManager();
  const shouldShowToast = !isWalletOpen && (isAdp || isBasket || isWishlist);

  return useCallback(() => {
    if (!shouldShowToast) {
      return;
    }

    showToast({
      type: 'WARNING',
      content: (
        <>
          <Text>{t('Oh no! Your coupon has expired.')}</Text>
          <Text>
            {t('We have updated the prices of the items in your basket.')}
          </Text>
        </>
      ),
    });
  }, [t, showToast, shouldShowToast]);
};

const useInvalidateRequests = () => {
  const {invalidateRequests} = useInvalidateQueryCache();
  const showCouponsExpiredToast = useShowCouponsExpiredToast();

  return useCallback(() => {
    invalidateRequests([
      CouponsService_GetWalletV2Lazy.method,
      WalletService_GetWalletLazy.method,
      BasketPageService_GetBasketPageBulkLazy.method,
      WishlistPageService_GetWishlistPageLazy.method,
    ]);

    showCouponsExpiredToast();
  }, [invalidateRequests, showCouponsExpiredToast]);
};

export const CouponsRefresh: React.FC = () => {
  const {data: couponsData, refetch: refetchCoupons} = useGetCoupons();
  const productId = useProductIdFromSlug();
  const {couponPromotionId: activeCouponPromotionId, setActivatedCoupon} =
    useActivatedCouponState(productId);
  const history = useHistory();
  const invalidateRequests = useInvalidateRequests();
  const nextUpdate = couponsData?.nextUpdateDate
    ? new Date(couponsData.nextUpdateDate).getTime()
    : undefined;

  const handleRefresh = useCallback(async () => {
    const currentCouponsSnapshot = JSON.stringify(couponsData?.coupon);
    const newCoupons = (await refetchCoupons()).data?.coupon;
    const newCouponsSnapshot = JSON.stringify(newCoupons);

    // if coupons are the same, we don't need to do anything
    if (currentCouponsSnapshot === newCouponsSnapshot) {
      return;
    }

    // check whether the selected coupon has expired
    // if it has, unselect it and remove if from query params
    if (activeCouponPromotionId) {
      const isActiveCouponStillAvailable = (newCoupons || []).some(
        coupon =>
          coupon.type?.active.couponPromotionId === activeCouponPromotionId,
      );

      if (!isActiveCouponStillAvailable) {
        setActivatedCoupon(undefined);
      }

      const queryParams = new URLSearchParams(history.location.search);

      if (queryParams.has(COUPON_PROMOTION_ID_QUERY_KEY)) {
        queryParams.delete(COUPON_PROMOTION_ID_QUERY_KEY);

        history.replace({
          search: queryParams.toString(),
        });
      }
    }

    invalidateRequests();
  }, [
    couponsData?.coupon,
    refetchCoupons,
    invalidateRequests,
    history,
    setActivatedCoupon,
    activeCouponPromotionId,
  ]);

  useEffect(() => {
    if (!nextUpdate) {
      return;
    }

    const timeNow = new Date().getTime();
    const timeDiff = nextUpdate - timeNow;

    const timeout = setTimeout(() => {
      // requestAnimationFrame is used to ensure that the refresh doesn't happen in background tabs.
      // The refresh will happen when the tab is active again.
      requestAnimationFrame(() => {
        startTransition(() => {
          handleRefresh();
        });
      });
    }, Math.min(timeDiff, MAX_TIMEOUT_WAIT_IN_MS));

    return () => clearTimeout(timeout);
  }, [nextUpdate, handleRefresh]);

  return null;
};
